<script setup>
    import SpeakersCard from '~/components/blocks/two-six/speakers/card.vue';

    const props = defineProps({
        session: {
            type: Object,
            required: true
        },
        variant: {
            type: String,
            default: 'page',
        },
    })

    const isDrawer = computed(() => props.variant === 'drawer')

    const activeIndex = ref(0)
    const speakersContainer = ref(null)
    const arrowLeft = ref(0)
    const arrowRight = ref(0)
    const overflows = ref(false)

    const updateOverflow = () => {
        const el = speakersContainer.value;
        if (!el) { overflows.value = false; return; }
        overflows.value = el.scrollWidth > el.clientWidth + 2;
    };

    const calculateVisibility = () => {
        if (!speakersContainer.value) return;
        if (!props.session.speakers?.length || props.session.speakers.length < 2) return;

        const TARGET_PERC = 0.3
        // Exclude the trailing spacer (it has no data-index) so it doesn't get
        // counted as the leftmost element and zero out activeIndex.
        const children = Array.from(speakersContainer.value.children).filter(
            (el) => el.hasAttribute('data-index')
        );
        const containerRect = speakersContainer.value.getBoundingClientRect();
        // Get the last and second to last children
        const lastChild = children[children.length - 1];

        let leftmostElement = null;
        let leftmostPosition = Infinity;
        let previousElementVisibility = 0;
        let previousElement = null;

        for (let i = 0; i < children.length; i++) {
            const currentElement = children[i];
            const currentRect = currentElement.getBoundingClientRect();

            // Calculate visibility percentage for current element
            let currentVisibleWidth = 0;
            if (currentRect.left < containerRect.right && currentRect.right > containerRect.left) {
                const visibleLeft = Math.max(currentRect.left, containerRect.left);
                const visibleRight = Math.min(currentRect.right, containerRect.right);
                currentVisibleWidth = visibleRight - visibleLeft;

                if (currentRect.left < leftmostPosition) {
                    leftmostPosition = currentRect.left;
                    leftmostElement = currentElement;
                }
            }

            let currentVisibilityPercentage = (currentVisibleWidth / currentRect.width);
            let inverseVisibilityPercentage = Math.max(TARGET_PERC, 1 - previousElementVisibility);

            // The last child is a special case, since it's previous sibling may never be 100% offscreen.
            if (lastChild === currentElement) {
                inverseVisibilityPercentage = currentVisibilityPercentage;
                if (inverseVisibilityPercentage > 0.90) previousElement.style.setProperty('--inverse-visibility', `${TARGET_PERC}`);
                if (currentVisibilityPercentage > 0.90) leftmostElement = currentElement
            }

            currentElement.style.setProperty('--current-visibility', `${currentVisibilityPercentage}`);
            currentElement.style.setProperty('--inverse-visibility', `${inverseVisibilityPercentage}`);

            previousElementVisibility = currentVisibilityPercentage;
            previousElement = currentElement;
        }

        if (leftmostElement) {
            activeIndex.value = parseInt(leftmostElement.getAttribute('data-index'), 10)
        }
    }

    const jump = (index) => {
        if (!speakersContainer.value) return;
        const target = document.querySelector(`.speaker[data-index="${index}"]`)
        if (target) {
            const smoothing = /iPad|iPhone|iPod/i.test(navigator.userAgent) ? 'smooth' : 'smooth'
            const offsetLeft = target.offsetLeft - speakersContainer.value.offsetLeft;
            speakersContainer.value.scrollTo({ left: offsetLeft, behavior: smoothing });
        }
    }

    const next = () => {
        const new_value = activeIndex.value < props.session.speakers.length - 1 ? activeIndex.value + 1 : 0
        jump(new_value)
    }

    const previous = () => {
        const new_value = activeIndex.value > 0 ? activeIndex.value - 1 : 0
        jump(new_value)
    }

    onMounted(() => {
        if (process.server) return;
        if (speakersContainer.value) speakersContainer.value.addEventListener('scroll', calculateVisibility, { passive: true })
        window.addEventListener('resize', updateOverflow);
        nextTick(() => { calculateVisibility(); updateOverflow(); })
    })

    onUnmounted(() => {
        if (process.server) return;
        if (speakersContainer.value) speakersContainer.value.removeEventListener('scroll', calculateVisibility)
        if (typeof window !== 'undefined') window.removeEventListener('resize', updateOverflow);
    })

</script>
<template>
    <section class="block-session-speakers overflow-hidden"
        :class="[
            { 'has-controls': session.speakers?.length > 1 },
            isDrawer
                ? '-mr-[var(--box-padding-lr-m)] md:-mr-[40px]'
                : 'bg-[var(--pink)] pb-[60px] md:pb-[90px]',
        ]"
        :style="`--arrow-left: ${arrowLeft}px; --arrow-right: ${arrowRight}px;`"
        v-if="session.speakers?.length > 0">
        <div class="flex flex-col gap-[40px] md:gap-[50px]">
            <div :class="isDrawer ? 'pr-[var(--box-padding-lr-m)] md:pr-[40px]' : 'px-[var(--box-padding-lr-m)] md:px-[var(--box-padding-lr)]'">
                <div class="border-t border-[var(--dark-pink)] pt-[40px] md:pt-[60px]">
                    <h4 class="m-0 font-hubspot-serif font-normal
                        leading-[1.2] tracking-normal text-[var(--magenta)]"
                        :class="isDrawer ? 'text-[30px] lg:text-[40px]' : 'text-[36px] lg:text-[48px]'">Session Speakers:</h4>
                </div>
            </div>

            <div class="carousel">
                <ul class="speakers flex flex-row flex-nowrap overflow-x-auto snap-x snap-mandatory
                    gap-[40px] md:gap-[64px]"
                    :class="isDrawer
                        ? 'pl-0 pr-[var(--box-padding-lr-m)] md:pr-[40px] scroll-pl-0'
                        : 'px-[var(--box-padding-lr-m)] md:px-[var(--box-padding-lr)] scroll-pl-[var(--box-padding-lr-m)] md:scroll-pl-[var(--box-padding-lr)]'"
                    ref="speakersContainer">
                    <li v-for="(speaker, index) in session.speakers" :key="index"
                        class="speaker snap-start shrink-0 transition-opacity duration-0 ease-in-out cursor-pointer"
                        :class="{ 'active': index === activeIndex }"
                        :data-index="index"
                        @click="jump(index)">
                        <SpeakersCard :speaker="speaker" :index="index" hide_featured />
                    </li>
                    <!-- Trailing spacer so the last speaker can snap-start to
                         the leftmost slot in the carousel viewport. -->
                    <li v-if="session.speakers?.length > 1"
                        aria-hidden="true"
                        class="shrink-0 w-[calc(100vw-280px-40px-var(--box-padding-lr-m))]
                            md:w-[calc(min(100vw,1120px)-(620px+40px+64px))]" />
                </ul>

                <div v-if="session.speakers?.length > 1 && overflows"
                    class="controls mt-[32px] md:mt-[40px]"
                    :class="isDrawer ? 'pr-[var(--box-padding-lr-m)] md:pr-[40px]' : 'px-[var(--box-padding-lr-m)] md:px-[var(--box-padding-lr)]'">
                    <FragmentsCarouselControls
                        :has-prev="activeIndex > 0"
                        :has-next="activeIndex < session.speakers.length - 1"
                        variant="magenta"
                        prev-label="Previous speaker"
                        next-label="Next speaker"
                        @prev="previous"
                        @next="next" />
                </div>
            </div>
        </div>
    </section>
</template>

<style>
    .block-session-speakers .speakers {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    .block-session-speakers .speakers::-webkit-scrollbar {
        display: none;
    }
</style>
